"use client";

import React, { useState } from "react";
import Link from "next/link";
import { Button } from "@/app/components/ui/button";
import { Zap, ArrowRight, Facebook, Linkedin, Youtube } from "lucide-react";
// import {
//   ContactModal,
//   ContactModalContent,
//   ContactModalTrigger,
// } from "@/app/components/ui/contact-modal";
import { Logo } from "@/app/components/Logo";
import { useToast } from "@/hooks/use-toast";

const services = [
  {
    title: "Compliance as a Service",
    href: "/services/compliance-risk-management",
  },
  {
    title: "Penetration Testing",
    href: "/services/penetration-testing-vulnerability-scanning",
  },
  {
    title: "Password Management",
    href: "/products/password-manager",
  },
  {
    title: "Risk Management",
    href: "/services/risk-management-framework",
  },
];

const resources = [
  { title: "Blog", href: "/blog" },
  // { title: "Documentation", href: "/docs" },
  // { title: "Case Studies", href: "/case-studies" },
  // { title: "Security Guides", href: "/guides" },
];

const company = [
  { title: "About", href: "/about" },
  { title: "Careers", href: "/careers" },
  { title: "Contact", href: "/contact" },
  { title: "Privacy Policy", href: "/privacy-policy" },
];

const Footer = () => {
  const [email, setEmail] = useState("");
  const [status, setStatus] = useState<"idle" | "loading">("idle");
  const { toast } = useToast();

  const handleSubscribe = async (e: React.FormEvent) => {
    e.preventDefault();
    setStatus("loading");

    try {
      const response = await fetch("/api/newsletter/subscribe", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({ email }),
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.error || "Failed to subscribe");
      }

      toast({
        title: "Successfully subscribed!",
        description:
          "You'll now receive our latest security insights and updates.",
        variant: "default",
      });

      setEmail("");
    } catch (error) {
      toast({
        title: "Subscription failed",
        description:
          error instanceof Error ? error.message : "Something went wrong",
        variant: "destructive",
      });
    } finally {
      setStatus("idle");
    }
  };

  return (
    <footer className="bg-[rgb(0,77,77)] relative">
      <div className="container mx-auto px-4 py-12">
        {/* Top section with logo and newsletter */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8 pb-12 relative">
          <div className="space-y-4 lg:col-span-2">
            <div className="max-w-[240px]">
              <Logo size="lg" />
            </div>
            <p className="text-gray-200 max-w-md pl-4">
              Empowering organizations with comprehensive cybersecurity
              solutions and compliance management for the digital age.
            </p>
          </div>
          <div className="lg:col-span-2">
            <div className="space-y-4 max-w-md ml-auto">
              <h3 className="text-white font-semibold">Stay updated</h3>
              <p className="text-gray-200">
                Subscribe to our newsletter for the latest security insights and
                updates.
              </p>
              <form onSubmit={handleSubscribe} className="space-y-2">
                <div className="flex gap-2">
                  <input
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder="Enter your email"
                    className="flex-1 rounded-md bg-transparent border border-[rgb(var(--color-neon-blue)_/_0.2)] px-4 py-2 text-gray-300 placeholder:text-gray-300 focus:border-[rgb(var(--color-neon-blue)_/_0.4)] focus:ring-0"
                    required
                  />
                  <Button
                    type="submit"
                    disabled={status === "loading"}
                    variant="neon"
                    size="default"
                    className="disabled:opacity-50"
                  >
                    {status === "loading" ? "Subscribing..." : "Subscribe"}
                  </Button>
                </div>
              </form>
            </div>
          </div>
          <div className="absolute bottom-0 w-full h-px bg-gradient-to-r from-transparent via-gray-800 to-transparent" />

          {/* Gradient border bottom */}
          {/* <div className="z-0 absolute bottom-0 w-full">
            <div className="absolute bottom-0 right-4 mt-[2px] flex h-8 items-end overflow-hidden w-full">
              <div className="flex -mb-px h-[2px] w-full">
                <div className="w-full flex-none blur-sm [background-image:linear-gradient(90deg,rgba(236,72,153,0)_0%,rgba(236,72,153,0.3)_32.29%,#0EA5E9_67.19%,rgba(56,189,248,0)_100%)]"></div>
                <div className="-ml-[100%] w-full flex-none blur-[1px] [background-image:linear-gradient(90deg,rgba(236,72,153,0)_0%,rgba(236,72,153,0.3)_32.29%,#0EA5E9_67.19%,rgba(56,189,248,0)_100%)]"></div>
              </div>
            </div>
          </div> */}
        </div>

        {/* Main footer links */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8 py-12 relative">
          <div>
            <h3 className="text-white font-semibold mb-4">Services</h3>
            <ul className="space-y-3">
              {services.map((service) => (
                <li key={service.title}>
                  <Link
                    href={service.href}
                    className="text-gray-200 hover:text-white transition-colors"
                  >
                    {service.title}
                  </Link>
                </li>
              ))}
            </ul>
          </div>
          <div>
            <h3 className="text-white font-semibold mb-4">Resources</h3>
            <ul className="space-y-3">
              {resources.map((item) => (
                <li key={item.title}>
                  <Link
                    href={item.href}
                    className="text-gray-200 hover:text-white transition-colors"
                  >
                    {item.title}
                  </Link>
                </li>
              ))}
            </ul>
          </div>
          <div>
            <h3 className="text-white font-semibold mb-4">Company</h3>
            <ul className="space-y-3">
              {company.map((item) => (
                <li key={item.title}>
                  <Link
                    href={item.href}
                    className="text-gray-200 hover:text-white transition-colors"
                  >
                    {item.title}
                  </Link>
                </li>
              ))}
            </ul>
          </div>
          <div>
            <h3 className="text-white font-semibold mb-4">Get Started</h3>
            <div className="space-y-4">
              <p className="text-gray-200">
                Ready to secure your organization? Schedule a consultation with
                our experts.
              </p>
              <Link
                href="/discover"
                className="w-full group inline-flex items-center justify-center gap-2 bg-[rgb(var(--color-neon-green))] hover:bg-[rgba(var(--color-neon-green),0.9)] text-black shadow-[0_0_15px_rgba(var(--color-neon-green),0.2)] hover:shadow-[0_0_20px_rgba(var(--color-neon-green),0.4)] transition-all px-6 py-3 rounded-md font-medium"
              >
                Book a Demo
                <ArrowRight className="h-4 w-4 group-hover:translate-x-1 transition-transform" />
              </Link>
            </div>
          </div>
        </div>

        {/* Standard horizontal separator */}
        <div className="w-full h-px bg-gray-800" />

        {/* Bottom section with social links and copyright */}
        <div className="pt-8 flex flex-col md:flex-row justify-between items-center gap-4">
          <div className="text-gray-200 text-sm">
            © {new Date().getFullYear()} Panoptic. All rights reserved.
          </div>
          <div className="flex gap-6">
            <Link
              href="https://www.facebook.com/PanopticITSolutions"
              className="text-gray-200 hover:text-[rgb(var(--color-neon-blue-light))] transition-colors"
              target="_blank"
              rel="noopener noreferrer"
              aria-label="Visit our Facebook page"
            >
              <Facebook className="h-5 w-5" />
            </Link>
            <Link
              href="https://www.linkedin.com/company/panoptic-it-solutions/"
              className="text-gray-200 hover:text-[rgb(var(--color-neon-blue-light))] transition-colors"
              target="_blank"
              rel="noopener noreferrer"
              aria-label="Visit our LinkedIn page"
            >
              <Linkedin className="h-5 w-5" />
            </Link>
            <Link
              href="https://www.youtube.com/@PanopticITSolutions"
              className="text-gray-200 hover:text-[rgb(var(--color-neon-blue-light))] transition-colors"
              target="_blank"
              rel="noopener noreferrer"
              aria-label="Visit our YouTube channel"
            >
              <Youtube className="h-5 w-5" />
            </Link>
          </div>
        </div>
      </div>

      {/* Top border gradient */}
      {/* <div className="z-0 absolute top-0 w-full">
        <div className="absolute top-0 right-4 mt-[2px] flex h-8 items-end overflow-hidden w-full">
          <div className="flex -mb-px h-[2px] w-full">
            <div className="w-full flex-none blur-sm [background-image:linear-gradient(90deg,rgba(56,189,248,0)_0%,#0EA5E9_32.29%,rgba(236,72,153,0.3)_67.19%,rgba(236,72,153,0)_100%)]"></div>
            <div className="-ml-[100%] w-full flex-none blur-[1px] [background-image:linear-gradient(90deg,rgba(56,189,248,0)_0%,#0EA5E9_32.29%,rgba(236,72,153,0.3)_67.19%,rgba(236,72,153,0)_100%)]"></div>
          </div>
        </div>
      </div> */}
    </footer>
  );
};

export default Footer;
